/**
 * useEmbedAudioChat — embed-app port of
 * `apps/wunderguide-web/src/hooks/useAudioChat.ts`. 1:1 with the canonical
 * webapp flow except:
 *
 *   - Skips the `/functions/v1/chat/sessions` + `/locations` bootstrap
 *     because InstanceContext already provisioned a `chatSessionId` and
 *     resolved an `agent` for this partner instance. We just consume them.
 *   - Pulls `httpClient` from the embed's own `../client` module.
 *
 * Everything else mirrors the webapp: manual `livekit-client` Room (not
 * the React components-wrapper), explicit mic acquisition + publishTrack,
 * Web Audio analysers feeding mic + bot audio levels, full teardown on
 * stop. Same connect lifecycle, same iOS-Safari AudioContext resume.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  RemoteTrack,
  RemoteAudioTrack,
  RemoteParticipant,
  RemoteTrackPublication,
  ConnectionState,
  createAudioAnalyser,
} from 'livekit-client';
import { httpClient } from '../client';
import { useInstance } from '../context/InstanceContext';
import { useChatSession } from '../context/ChatSessionContext';
import type { ChatContentItem } from '@wg/types';

/**
 * A batch of content items the agent pushed over the LiveKit room data
 * channel during a voice session (topic `wg.content`). The embed app has no
 * Socket.IO client — unlike the RN app it can't reach the voice gateway — so
 * the room data channel is the ONLY path voice-session cards/HTML have into
 * the embed. ChatView drains new batches into the chat thread.
 */
export interface VoiceContentBatch {
  /** Stable id — ChatView tracks which batches it has already appended. */
  id: string;
  /** Normalized to the ChatContentItem wire shape the renderers expect. */
  items: ChatContentItem[];
  at: string;
}

export interface UseEmbedAudioChat {
  isListening: boolean;
  isConnecting: boolean;
  hasRemoteParticipant: boolean;
  micAudioLevel: number;
  botAudioLevel: number;
  isBotWorking: boolean;
  permissionState: 'unknown' | 'prompt' | 'granted' | 'denied';
  toggleListening: () => Promise<void>;
  startListening: () => Promise<void>;
  stopListening: () => void;
  /** Content items received over the room data channel this voice session. */
  voiceContentBatches: VoiceContentBatch[];
}

interface SessionStartResponse {
  token: string;
  lkUrl: string;
  serverHints?: Record<string, unknown>;
}

function computeRms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i];
    sum += v * v;
  }
  return Math.sqrt(sum / samples.length);
}

/**
 * Normalize whatever the agent published on the `wg.content` data channel
 * into the ChatContentItem wire shape the embed renderers consume.
 *
 *  - The live voice agent (livekit-agent webdata.py) renders cards to HTML
 *    server-side and ships `{ id, src: { html } }` — wrap each as an `html`
 *    content item so HtmlRenderer can display it.
 *  - If an item already carries a string `type` (e.g. once voice is routed
 *    through the Brain, which emits typed ChatContentItems), pass it through
 *    untouched so the native renderers (CardRenderer, AvailabilityRenderer…)
 *    pick it up.
 *
 * Anything unrecognised is dropped rather than risking a renderer crash.
 */
function normalizeVoiceItems(raw: unknown[]): ChatContentItem[] {
  const out: ChatContentItem[] = [];
  for (const it of raw) {
    if (!it || typeof it !== 'object') continue;
    const obj = it as Record<string, unknown>;
    if (typeof obj.type === 'string') {
      out.push(obj as unknown as ChatContentItem);
      continue;
    }
    const src = obj.src as { html?: unknown } | undefined;
    if (src && typeof src.html === 'string' && src.html.trim()) {
      out.push({ type: 'html', html: src.html, sandbox: 'strict' });
    }
  }
  return out;
}

export function useEmbedAudioChat(): UseEmbedAudioChat {
  const { effectiveLocale, instanceId, parentOrigin, config } = useInstance();
  const { chatSessionId, agent } = useChatSession();
  // The URL param `instance` carries the public_token (public, single-use
  // identifier the partner pasted into their snippet). The resolved UUID is
  // returned by /partners/v1/embedded/resolve as cfg.instance_id. We send
  // both so the server can cross-check the binding.
  const publicToken = instanceId; // URL param name is historical; value is the token.
  const resolvedInstanceId = config?.instance_id ?? null;

  const [isListening, setIsListening] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [hasRemoteParticipant, setHasRemoteParticipant] = useState(false);
  const [micAudioLevel, setMicAudioLevel] = useState(0);
  const [botAudioLevel, setBotAudioLevel] = useState(0);
  const [isBotWorking, setIsBotWorking] = useState(false);
  const [permissionState, setPermissionState] =
    useState<UseEmbedAudioChat['permissionState']>('unknown');
  const [voiceContentBatches, setVoiceContentBatches] = useState<
    VoiceContentBatch[]
  >([]);

  const roomRef = useRef<Room | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  // Hidden <audio> elements appended in TrackSubscribed. Tracked explicitly
  // because relying on TrackUnsubscribed for cleanup is brittle: tearDown
  // nulls roomRef immediately after disconnect(), so unsubscribe events fire
  // on a Room being collected and our remove() handlers may never run. Without
  // explicit cleanup the elements pile up on document.body across activations.
  // Ports the apps/wunderguide-web SPA fix (commit b440a55c).
  const audioElsRef = useRef<HTMLAudioElement[]>([]);

  type BotAnalyser = {
    calculateVolume: () => number;
    cleanup: () => Promise<void>;
  };
  const botAnalysersRef = useRef(new Map<string, BotAnalyser>());

  const tearDown = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    micAnalyserRef.current?.disconnect();
    micAnalyserRef.current = null;
    for (const a of botAnalysersRef.current.values()) {
      a.cleanup().catch(() => {});
    }
    botAnalysersRef.current.clear();
    // Explicitly remove every hidden audio element we created for remote
    // tracks (see audioElsRef comment above).
    for (const el of audioElsRef.current) {
      try {
        el.pause();
        (el as unknown as { srcObject: MediaStream | null }).srcObject = null;
        el.remove();
      } catch {
        /* swallow */
      }
    }
    audioElsRef.current = [];
    // Close the AudioContext on a new microtask so the already-scheduled RAF
    // tick can drain before the context goes away. Without this we hit
    // half-closed-context warnings on the next session.
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx) {
      Promise.resolve().then(() => ctx.close().catch(() => {}));
    }
    if (localStreamRef.current) {
      for (const t of localStreamRef.current.getTracks()) t.stop();
      localStreamRef.current = null;
    }
    if (roomRef.current) {
      const room = roomRef.current;
      roomRef.current = null;
      // disconnect() fires TrackUnsubscribed for each subscribed track before
      // resolving; we already cleaned up <audio> elements above, so this is
      // just to let the SFU release the publish slot. Don't race the next
      // connect — defer inside a microtask.
      Promise.resolve().then(() => room.disconnect().catch(() => {}));
    }
    setHasRemoteParticipant(false);
    setMicAudioLevel(0);
    setBotAudioLevel(0);
    setIsBotWorking(false);
    setVoiceContentBatches([]);
  }, []);

  useEffect(() => () => tearDown(), [tearDown]);

  // REST session start → LiveKit creds. Same payload shape as RN/web.
  // Failures used to silently no-op the call (mic-only fallback), which
  // turned voice into a black box during the 2026-05-15 demo prep: no
  // server-side trace AND no client-side visible error. Now: log status,
  // body, request-id, and surface the failure to the parent bridge so the
  // launcher can hide / message about it.
  const restStartSession = useCallback(
    async (sessionId: string): Promise<SessionStartResponse | null> => {
      const reqId = `voice-start-${sessionId.slice(0, 8)}-${Date.now()}`;
      // Per-mic-activation fencing token (mirrors RN + wunderguide-web). A fresh
      // value each /start makes the server treat this tap as the current
      // activation generation, so exactly one agent (the latest tap's) stays in
      // the room. Previously hardcoded null, which forced the legacy claim path.
      const activationId = `${sessionId}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const payload = {
        regionHint: null,
        lang: effectiveLocale || navigator.language || 'en-US',
        e2ee: null,
        capabilities: { voice: true },
        agentId: agent?.id,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        destinationLocation: null,
        activationId,
        // Principal binding — the embed surface always sends these so the
        // server can stamp instance/policy onto session_ctx. The server
        // cross-checks publicToken↔instanceId.
        appSurface: 'embed',
        publicToken,
        instanceId: resolvedInstanceId,
        parentOrigin,
        appVersion: __WG_EMBED_VERSION__,
        sdkVersion: __WG_EMBED_VERSION__,
      };
      // eslint-disable-next-line no-console
      console.info('[wg-voice] /sessions/v1/start →', {
        reqId,
        sessionId,
        activationId,
        publicToken: publicToken ? `…${publicToken.slice(-8)}` : null,
        instanceId: resolvedInstanceId,
        agentId: agent?.id ?? null,
      });
      try {
        // NOTE: do NOT add custom request headers (e.g. X-WG-Request-Id)
        // here without first widening Caddy's `Access-Control-Allow-Headers`
        // — the CORS preflight from dev.embed.app.wunderguide.com →
        // dev.api.dodecki.com rejects unknown headers and the whole call
        // turns into a "Network Error" with no status. Trace via the
        // reqId in the console logs instead. See feedback_web_api_timeout
        // (same shape as the User-Agent issue on iPhone).
        const { data, status } = await httpClient.post<SessionStartResponse>(
          `/sessions/v1/${sessionId}/start`,
          payload,
        );
        // eslint-disable-next-line no-console
        console.info('[wg-voice] /sessions/v1/start ✓', {
          reqId,
          status,
          hasToken: Boolean(data?.token),
          hasLkUrl: Boolean(data?.lkUrl),
        });
        if (!data?.token || !data?.lkUrl) {
          // Shape regression — surface as failure so we don't silently
          // attempt a half-baked LiveKit connect.
          throw new Error(
            `start response missing token/lkUrl (token=${Boolean(data?.token)} lkUrl=${Boolean(data?.lkUrl)})`,
          );
        }
        return data;
      } catch (err: unknown) {
        const e = err as {
          response?: { status?: number; data?: unknown };
          message?: string;
        };
        const status = e?.response?.status;
        const body =
          e?.response?.data &&
          (typeof e.response.data === 'string'
            ? e.response.data.slice(0, 300)
            : JSON.stringify(e.response.data).slice(0, 300));
        // eslint-disable-next-line no-console
        console.error('[wg-voice] /sessions/v1/start FAILED', {
          reqId,
          status,
          body,
          message: e?.message,
          publicTokenSuffix: publicToken ? publicToken.slice(-8) : null,
          instanceId: resolvedInstanceId,
        });
        return null;
      }
    },
    [agent?.id, effectiveLocale, publicToken, resolvedInstanceId, parentOrigin],
  );

  // Mic permission. Returns the live MediaStream so we publish the same
  // tracks that drive the local analyser (no second getUserMedia prompt).
  const acquireMic = useCallback(async (): Promise<MediaStream | null> => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setPermissionState('denied');
      return null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      setPermissionState('granted');
      localStreamRef.current = stream;
      return stream;
    } catch (err) {
      const e = err as { name?: string };
      const denied =
        e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError';
      setPermissionState(denied ? 'denied' : 'prompt');
      return null;
    }
  }, []);

  const ensureAudioContext = useCallback((): AudioContext | null => {
    if (audioCtxRef.current) return audioCtxRef.current;
    type Win = Window &
      typeof globalThis & {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      };
    const w = window as Win;
    const Ctor = w.AudioContext || w.webkitAudioContext;
    if (!Ctor) return null;
    const ctx: AudioContext = new Ctor();
    audioCtxRef.current = ctx;
    // iOS Safari starts AudioContexts in `suspended` until a user gesture
    // *and* an explicit resume(). Without this, analysers read silence
    // forever and MicButton ripples never appear on iPhone.
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  }, []);

  const attachMicAnalyser = useCallback(
    (stream: MediaStream) => {
      const ctx = ensureAudioContext();
      if (!ctx) return;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.85;
      src.connect(analyser);
      micAnalyserRef.current = analyser;
    },
    [ensureAudioContext],
  );

  // Per-track bot analyser via LK's createAudioAnalyser helper. The agent
  // publishes microphone-source AND unknown-source tracks; the SFU only
  // computes audioLevel for microphone, so we tap each track manually.
  const attachBotAnalyser = useCallback((track: RemoteTrack) => {
    if (track.kind !== Track.Kind.Audio) return;
    const sid = track.sid;
    if (!sid || botAnalysersRef.current.has(sid)) return;
    try {
      const { calculateVolume, cleanup } = createAudioAnalyser(
        track as RemoteAudioTrack,
        { cloneTrack: false, smoothingTimeConstant: 0.6 },
      );
      botAnalysersRef.current.set(sid, { calculateVolume, cleanup });
    } catch (err) {
      console.warn('[wg-voice] createAudioAnalyser failed', err);
    }
  }, []);

  const detachBotAnalyser = useCallback((track: RemoteTrack) => {
    const sid = track.sid;
    if (!sid) return;
    const handle = botAnalysersRef.current.get(sid);
    if (!handle) return;
    botAnalysersRef.current.delete(sid);
    handle.cleanup().catch(() => {});
  }, []);

  const startAnalyserLoop = useCallback(() => {
    const micBuf = new Float32Array(1024);
    const tick = () => {
      if (micAnalyserRef.current) {
        micAnalyserRef.current.getFloatTimeDomainData(micBuf);
        setMicAudioLevel(Math.min(1, computeRms(micBuf) * 4));
      }
      let botLvl = 0;
      for (const a of botAnalysersRef.current.values()) {
        const v = a.calculateVolume();
        if (v > botLvl) botLvl = v;
      }
      botLvl = Math.min(1, botLvl * 2);
      setBotAudioLevel(botLvl);
      setIsBotWorking(botLvl > 0.05);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const connectLiveKit = useCallback(
    async (creds: SessionStartResponse, micStream: MediaStream) => {
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: { dtx: true, red: true },
      });
      roomRef.current = room;

      room.on(RoomEvent.ParticipantConnected, (_p: RemoteParticipant) => {
        setHasRemoteParticipant(true);
      });
      room.on(RoomEvent.ParticipantDisconnected, () => {
        setHasRemoteParticipant(
          (roomRef.current?.remoteParticipants?.size ?? 0) > 0,
        );
      });
      room.on(
        RoomEvent.TrackSubscribed,
        (
          track: RemoteTrack,
          _pub: RemoteTrackPublication,
          _p: RemoteParticipant,
        ) => {
          if (track.kind === Track.Kind.Audio) {
            const el = track.attach() as HTMLAudioElement;
            el.style.display = 'none';
            document.body.appendChild(el);
            audioElsRef.current.push(el);
            attachBotAnalyser(track);
          }
        },
      );
      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        const detached = track.detach();
        for (const el of detached) {
          el.remove();
          const idx = audioElsRef.current.indexOf(el as HTMLAudioElement);
          if (idx >= 0) audioElsRef.current.splice(idx, 1);
        }
        detachBotAnalyser(track);
      });
      room.on(RoomEvent.ConnectionStateChanged, (_state: ConnectionState) => {
        // no-op; useful for debugging
      });

      // Agent → embed content delivery. The embed has no Socket.IO client
      // (unlike the RN app, which reaches the voice gateway), so the LiveKit
      // room data channel is the only path voice-session cards/HTML have
      // into the embed chat. The worker publishes them on the `wg.content`
      // topic (its control signals use `agent` / `client` — ignored here).
      room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
        if (topic !== 'wg.content') return;
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload)) as {
            type?: string;
            items?: unknown[];
          };
          if (msg?.type !== 'content_items' || !Array.isArray(msg.items)) {
            return;
          }
          const items = normalizeVoiceItems(msg.items);
          if (items.length === 0) return;
          setVoiceContentBatches(prev => [
            ...prev,
            {
              id: `voice-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 8)}`,
              items,
              at: new Date().toISOString(),
            },
          ]);
        } catch (err) {
          console.warn('[wg-voice] data channel parse failed', err);
        }
      });

      await room.connect(creds.lkUrl, creds.token);

      const [audioTrack] = micStream.getAudioTracks();
      if (audioTrack) {
        await room.localParticipant.publishTrack(audioTrack, {
          name: 'microphone',
          source: Track.Source.Microphone,
        });
      }

      if ((room.remoteParticipants?.size ?? 0) > 0) {
        setHasRemoteParticipant(true);
      }
    },
    [attachBotAnalyser, detachBotAnalyser],
  );

  const startListening = useCallback(async () => {
    if (isListening || isConnecting) {
      // eslint-disable-next-line no-console
      console.info('[wg-voice] startListening skipped — already running', {
        isListening,
        isConnecting,
      });
      return;
    }
    if (!chatSessionId) {
      // eslint-disable-next-line no-console
      console.error(
        '[wg-voice] startListening BLOCKED — no chatSessionId. AuthProvider may still be bootstrapping (createGuest in flight); the launcher should disable mic until ChatSessionContext is ready.',
      );
      return;
    }
    // eslint-disable-next-line no-console
    console.info('[wg-voice] startListening BEGIN', { chatSessionId });
    setIsConnecting(true);
    // Critical: create + resume AudioContext synchronously inside the
    // user-gesture callback. After any await, iOS Safari has closed the
    // gesture window and the context stays `suspended`.
    ensureAudioContext();
    try {
      const micStream = await acquireMic();
      if (!micStream) {
        // eslint-disable-next-line no-console
        console.error(
          '[wg-voice] startListening ABORT — mic acquisition returned null. Permission was denied OR the browser does not expose getUserMedia. Voice cannot run; the user must grant mic permission (browser site settings → Microphone → Allow for embed-poc.wunderguide.com).',
        );
        setIsConnecting(false);
        return;
      }
      attachMicAnalyser(micStream);
      startAnalyserLoop();

      const creds = await restStartSession(chatSessionId);
      if (!creds) {
        // /start failed — server side has full trace (see restStartSession
        // console.error). DO NOT fall back to mic-only-no-agent silently;
        // that's what caused the 2026-05-15 black-box failures. Tear down
        // mic and surface a hard error so the UI can prompt for retry.
        // eslint-disable-next-line no-console
        console.error(
          '[wg-voice] startListening FAILED — /sessions/v1/start did not return creds. See preceding [wg-voice] /sessions/v1/start FAILED log for the server response. Tearing down mic; user must retry.',
        );
        try {
          micStream.getTracks().forEach(t => t.stop());
        } catch {
          /* swallow */
        }
        localStreamRef.current = null;
        setIsConnecting(false);
        setIsListening(false);
        return;
      }

      await connectLiveKit(creds, micStream);
      // eslint-disable-next-line no-console
      console.info('[wg-voice] startListening OK', {
        chatSessionId,
        lkUrl: creds.lkUrl,
      });
      setIsConnecting(false);
      setIsListening(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[wg-voice] startListening EXCEPTION', err);
      setIsConnecting(false);
      setIsListening(false);
    }
  }, [
    isListening,
    isConnecting,
    chatSessionId,
    ensureAudioContext,
    acquireMic,
    attachMicAnalyser,
    startAnalyserLoop,
    restStartSession,
    connectLiveKit,
  ]);

  const stopListening = useCallback(() => {
    setIsListening(false);
    setIsConnecting(false);
    tearDown();
  }, [tearDown]);

  const toggleListening = useCallback(async () => {
    if (isListening || isConnecting) stopListening();
    else await startListening();
  }, [isListening, isConnecting, startListening, stopListening]);

  return {
    isListening,
    isConnecting,
    hasRemoteParticipant,
    micAudioLevel,
    botAudioLevel,
    isBotWorking,
    permissionState,
    toggleListening,
    startListening,
    stopListening,
    voiceContentBatches,
  };
}

export default useEmbedAudioChat;
